import InternalChatAPI from 'dashboard/api/internalChat';
import {
  applyReaction,
  sortByCreatedAt,
  upsertMessageById,
  mergeMessageRevision,
  reconcileMessage,
} from './internalChatMessageHelpers';
import {
  inMessageWindow,
  mergeWindowMeta,
  revalidateMessageWindow,
} from './internalChatMessageWindow';

export const threadState = (generation = 0) => ({
  generation,
  scope: null,
  isOpening: false,
  isLoadingOlder: false,
  isLoadingNewer: false,
  isRefreshing: false,
  refreshQueued: false,
  showLatestQueued: false,
  error: null,
  events: [],
});

export const clearThread = state => {
  state.thread = null;
  state.threadState = threadState(state.threadState.generation + 1);
};

const parentWithCount = (parent, incoming) => ({
  ...mergeMessageRevision(parent, incoming),
  replies_count: Math.max(
    parent?.replies_count || 0,
    incoming.replies_count || 0
  ),
});

const applyMessage = (thread, message) => {
  if (thread.parent.id === message.id)
    return { ...thread, parent: parentWithCount(thread.parent, message) };
  if (message.parent_id !== thread.parent.id) return thread;
  const replies = thread.replies;
  if (!inMessageWindow(message, replies, thread.meta)) return thread;
  const updated = upsertMessageById(replies, message).sort(sortByCreatedAt);
  const last = updated.at(-1);
  return {
    ...thread,
    replies: updated,
    meta: { ...thread.meta, after: last.created_at, after_id: last.id },
  };
};

const applyEvent = (thread, event) =>
  event.message
    ? applyMessage(thread, event.message)
    : {
        ...thread,
        parent: applyReaction(thread.parent, event),
        replies: thread.replies.map(message => applyReaction(message, event)),
      };

export const threadEvent = (state, event) => {
  const { scope, isOpening, isLoadingOlder, isLoadingNewer, isRefreshing } =
    state.threadState;
  const channelId = event.message?.channel_id || event.channelId;
  if (scope && channelId && channelId !== scope.channelId) return;
  if (scope && (isOpening || isLoadingOlder || isLoadingNewer || isRefreshing))
    state.threadState.events.push(event);
  if (state.thread) state.thread = applyEvent(state.thread, event);
};

const requestOwner = ({ state, rootGetters }) => {
  const { generation, scope } = state.threadState;
  return () =>
    generation === state.threadState.generation &&
    scope.accountId === rootGetters.getCurrentAccountId &&
    scope.channelId === state.selectedChannelId;
};

export const threadMutations = {
  OPEN_THREAD(state, { scope, parent }) {
    clearThread(state);
    Object.assign(state.threadState, { scope, isOpening: true });
    if (parent)
      state.thread = {
        parent: reconcileMessage(state, parent),
        replies: [],
        meta: {},
      };
  },
  THREAD_REQUEST(state, values) {
    Object.assign(state.threadState, values);
  },
  THREAD_PAGE(
    state,
    { page, append = false, newer = false, refreshed = false }
  ) {
    const previous = state.thread;
    const parent = reconcileMessage(state, page.parent);
    const replies = page.replies.map(message =>
      reconcileMessage(state, message)
    );
    const thread = {
      ...page,
      parent: {
        ...parentWithCount(previous?.parent, parent),
        reactions: parent.reactions,
        reactionRemovals: parent.reactionRemovals,
      },
      replies: (append
        ? replies.reduce(upsertMessageById, previous.replies)
        : replies
      )
        .slice()
        .sort(sortByCreatedAt),
      meta: append
        ? mergeWindowMeta(previous.meta, page.meta, newer)
        : page.meta,
    };
    if (refreshed && previous?.replies.length && !previous.meta.context_id) {
      Object.assign(thread.meta, {
        has_more: previous.meta.has_more,
        before: previous.meta.before,
        before_id: previous.meta.before_id,
      });
    }
    state.thread = state.threadState.events.reduce(applyEvent, thread);
  },
};

const handleError = (commit, error) => {
  if ([401, 403, 404].includes(error.response?.status))
    commit('SET_THREAD', null);
  else commit('THREAD_REQUEST', { error });
};

const finishRequest = async ({ state, commit, dispatch }, current, flags) => {
  if (!current()) return;
  commit('THREAD_REQUEST', { ...flags, events: [] });
  if (state.threadState.showLatestQueued) {
    try {
      await dispatch('showLatestThread');
    } catch {
      // The request exposes its error without changing a confirmed send.
    }
    return;
  }
  if (!state.threadState.refreshQueued) return;
  try {
    await dispatch('refreshThread');
  } catch {
    // The refresh action records its error; preserve the original request result.
  }
};

export const threadActions = {
  async showLatestThread(context) {
    const { state, commit } = context;
    const { threadState: status } = state;
    if (!state.thread) return;
    if (
      status.isOpening ||
      status.isRefreshing ||
      status.isLoadingOlder ||
      status.isLoadingNewer
    ) {
      commit('THREAD_REQUEST', { showLatestQueued: true });
      return;
    }
    const current = requestOwner(context);
    commit('THREAD_REQUEST', {
      isRefreshing: true,
      showLatestQueued: false,
      error: null,
      events: [],
    });
    try {
      const { data: page } = await InternalChatAPI.getThread(
        status.scope.channelId,
        status.scope.messageId
      );
      if (current()) commit('THREAD_PAGE', { page });
    } catch (error) {
      if (current()) handleError(commit, error);
      throw error;
    } finally {
      await finishRequest(context, current, { isRefreshing: false });
    }
  },
  async fetchThread(context, { channelId, messageId, parent, params }) {
    const { commit, dispatch, rootGetters } = context;
    commit('OPEN_THREAD', {
      scope: {
        accountId: rootGetters.getCurrentAccountId,
        channelId,
        messageId,
      },
      parent,
    });
    const current = requestOwner(context);
    try {
      const response = params
        ? await InternalChatAPI.getThread(channelId, messageId, params)
        : await InternalChatAPI.getThread(channelId, messageId);
      if (!current()) return null;
      commit('THREAD_PAGE', { page: response.data });
      await dispatch('fetchDraft', { channelId, parentId: messageId });
      return current() ? context.state.thread : null;
    } catch (error) {
      if (current()) handleError(commit, error);
      throw error;
    } finally {
      await finishRequest(context, current, { isOpening: false });
    }
  },
  async loadOlderThread(context) {
    const { state, commit } = context;
    const { thread, threadState: status } = state;
    if (
      !thread?.meta.has_more ||
      status.isLoadingOlder ||
      status.isLoadingNewer ||
      status.isRefreshing ||
      status.isOpening
    )
      return;
    const current = requestOwner(context);
    commit('THREAD_REQUEST', { isLoadingOlder: true, error: null, events: [] });
    try {
      const response = await InternalChatAPI.getThread(
        status.scope.channelId,
        thread.parent.id,
        {
          before: thread.meta.before,
          before_id: thread.meta.before_id,
        }
      );
      if (current())
        commit('THREAD_PAGE', { page: response.data, append: true });
    } catch (error) {
      if (current()) handleError(commit, error);
      throw error;
    } finally {
      await finishRequest(context, current, { isLoadingOlder: false });
    }
  },
  async loadNewerThread(context) {
    const { state, commit } = context;
    const { thread, threadState: status } = state;
    if (
      !thread?.meta.has_more_after ||
      status.isLoadingOlder ||
      status.isLoadingNewer ||
      status.isRefreshing ||
      status.isOpening
    )
      return;
    const current = requestOwner(context);
    commit('THREAD_REQUEST', { isLoadingNewer: true, error: null, events: [] });
    try {
      const response = await InternalChatAPI.getThread(
        status.scope.channelId,
        thread.parent.id,
        {
          after: thread.meta.after,
          after_id: thread.meta.after_id,
        }
      );
      if (current())
        commit('THREAD_PAGE', {
          page: response.data,
          append: true,
          newer: true,
        });
    } catch (error) {
      if (current()) handleError(commit, error);
      throw error;
    } finally {
      await finishRequest(context, current, { isLoadingNewer: false });
    }
  },
  async refreshThread(context) {
    const { state, commit } = context;
    const { thread, threadState: status } = state;
    if (!status.scope) return;
    if (
      status.isOpening ||
      status.isRefreshing ||
      status.isLoadingOlder ||
      status.isLoadingNewer
    ) {
      commit('THREAD_REQUEST', { refreshQueued: true });
      return;
    }
    if (!thread) return;
    const current = requestOwner(context);
    const { channelId, messageId } = status.scope;
    commit('THREAD_REQUEST', {
      isRefreshing: true,
      refreshQueued: false,
      error: null,
      events: [],
    });
    try {
      if (thread.meta.context_id && thread.replies.length) {
        const page = await revalidateMessageWindow({
          records: thread.replies,
          meta: thread.meta,
          current,
          field: 'replies',
          getPage: async params =>
            (await InternalChatAPI.getThread(channelId, messageId, params))
              .data,
          getMessage: async id =>
            (await InternalChatAPI.getMessage(channelId, id)).data,
        });
        if (current() && page) commit('THREAD_PAGE', { page, refreshed: true });
        return;
      }
      const { data: latest } = await InternalChatAPI.getThread(
        channelId,
        messageId
      );
      if (!current()) return;
      let collected = latest.replies;
      if (thread.replies.length) {
        const { data: earliest } = await InternalChatAPI.getMessage(
          channelId,
          thread.replies[0].id
        );
        if (!current()) return;
        const edge = latest.replies.at(-1);
        collected = [earliest];
        let cursor = earliest;
        while (edge && sortByCreatedAt(cursor, edge) < 0) {
          // Each cursor depends on the previous page; parallel requests would leave gaps.
          // eslint-disable-next-line no-await-in-loop
          const { data: page } = await InternalChatAPI.getThread(
            channelId,
            messageId,
            {
              after: cursor.created_at,
              after_id: cursor.id,
            }
          );
          if (!current()) return;
          const next = page.replies.at(-1);
          if (!next || sortByCreatedAt(next, cursor) <= 0)
            throw new Error('Thread cursor failed to advance');
          collected.push(
            ...page.replies.filter(item => sortByCreatedAt(item, edge) <= 0)
          );
          cursor = next;
        }
      }
      commit('THREAD_PAGE', {
        page: { ...latest, replies: collected },
        refreshed: true,
      });
    } catch (error) {
      if (current()) handleError(commit, error);
      throw error;
    } finally {
      await finishRequest(context, current, { isRefreshing: false });
    }
  },
};
